import React, { Dispatch, SetStateAction } from 'react';

import { TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { ReactComponent as ColumnSortIcon } from 'assets/icons/ColumnSort.svg';
import { ReactComponent as ColumnSortAscIcon } from 'assets/icons/ColumnSortActiveAsc.svg';
import { ReactComponent as ColumnSortDescIcon } from 'assets/icons/ColumnSortActiveDesc.svg';
import { Order } from 'utils/dataTableUtils';
import { JBColors } from '@bjb-gcrm/gcrm-frontend-components';
import { ColumnDefinition, HeadCellFilterType } from 'components/DataTable/DataTableTypes';
import { DataTableFilter } from 'components/DataTableFilters/DataTableFilter';

const styles = {
  sortIcon: {
    width: '16px',
  },
};

type Props<T> = {
  data: any;
  headCells: ColumnDefinition<T>[];
  orderBy: string;
  order: Order;
  setOrder: Dispatch<SetStateAction<Order>>;
  setOrderBy: Dispatch<SetStateAction<string>>;
  handleSort: (selectedFilters: { [key in keyof T]: string }[]) => void;
  handleSearch: (searchString: React.SetStateAction<string>, searchKey: keyof T) => void;
};

const EnhancedTableHead = <T,>({
  data,
  headCells,
  orderBy,
  order,
  setOrder,
  setOrderBy,
  handleSort,
  handleSearch,
}: Props<T>) => {
  const onRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: string) => () => {
    onRequestSort(property);
  };

  const getIconComponent = (id: keyof T) => {
    if (orderBy === id) {
      return order === 'asc' ? (
        <ColumnSortAscIcon style={styles.sortIcon} />
      ) : (
        <ColumnSortDescIcon style={styles.sortIcon} />
      );
    } else {
      return <ColumnSortIcon style={styles.sortIcon} />;
    }
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          return (
            <TableCell
              key={headCell.id.toString()}
              sortDirection={orderBy === headCell.id ? order : false}
              id={`tablehead-${String(headCell.id)}`}
              style={{
                width: headCell.width,
                color: headCell.color,
                borderBottom: `none`,
              }}
            >
              {headCell.filter === HeadCellFilterType.SORT && (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id.toString())}
                  IconComponent={() => getIconComponent(headCell.id)}
                  color={JBColors.support.greenSmoke[80]}
                >
                  <Typography color={JBColors.support.greenSmoke[80]} sx={{ fontWeight: 400 }}>
                    {headCell.label}
                  </Typography>
                </TableSortLabel>
              )}
              {(headCell.filter === HeadCellFilterType.CHECKBOXFILTER ||
                headCell.filter === HeadCellFilterType.SEARCHFILTER) && (
                <DataTableFilter
                  data={data}
                  headCell={headCell}
                  handleSort={handleSort}
                  handleSearch={handleSearch}
                  shouldFormatEnum={headCell.shouldFormatEnum}
                />
              )}
              {headCell.filter === HeadCellFilterType.EMPTY && (
                <Typography color={JBColors.support.greenSmoke[80]} sx={{ fontWeight: 400 }}>
                  {headCell.label}
                </Typography>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
export default EnhancedTableHead;
